import React, { useEffect, useMemo, useReducer } from 'react';
import { AlertTriangle, Radio, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  api, displayName, fmtInt, fmtPct, humanize, isoDay, pctOf, timeAgo, useQuery, useUsers,
} from './lib';
import { EmptyState, ErrorState, Panel, RowsSkeleton, StatCard, StatGrid, StatSkeletons } from './ui';

/* Onboarding as a funnel: everyone who started, how far each got, and who is
   in it right now. Before sign-in people are anonymous devices; the server
   follows them across sign-in (see /admin/kpi/onboarding-funnel). */

const STAGES = [
  ['landing', 'Intro screens'],
  ['questions', 'Questions'],
  ['personalizing', 'Personalizing'],
  ['paywall', 'Paywall'],
];
const DEVICE = { ios: 'iPhone', android: 'Android', web: 'Web' };
const LIVE_EVERY_MS = 10_000;
const FUNNEL_EVERY_MS = 60_000;

/* Re-run `refetch` every `ms` while the tab is visible, and on coming back. */
function usePolling(refetch, ms, enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;
    const tick = () => { if (document.visibilityState === 'visible') refetch(); };
    const id = setInterval(tick, ms);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [refetch, ms, enabled]);
}

/* "12 s ago", kept current. */
function Since({ at }) {
  const [, tick] = useReducer((n) => n + 1, 0);
  useEffect(() => {
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, []);
  if (!at) return '—';
  const s = Math.max(0, Math.round((Date.now() - at) / 1000));
  return s < 60 ? `${s} s ago` : timeAgo(new Date(at));
}

function LiveDot({ on = true }) {
  return (
    <span className="relative flex h-2 w-2">
      {on && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-60" />}
      <span className={cn('relative inline-flex h-2 w-2 rounded-full', on ? 'bg-emerald-500' : 'bg-muted-foreground/40')} />
    </span>
  );
}

/* ── Funnel ──────────────────────────────────────────────────────────────── */

/* One step: the bar is the share of starters who got this far; the faint
   piece after it is who this step lost. */
function FunnelRow({ label, count, prev, base, here = 0 }) {
  const lost = Math.max(0, prev - count);
  const drop = pctOf(lost, prev) || 0;
  const width = base ? (count / base) * 100 : 0;
  const lostWidth = base ? (lost / base) * 100 : 0;
  const severity = drop > 25 ? 'critical' : drop > 10 ? 'warning' : null;

  // On phones the label and numbers share a line and the bar runs full width under them.
  return (
    <li
      tabIndex={0}
      className="group relative grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 rounded-md px-2 py-1.5 outline-none hover:bg-secondary/60 focus-visible:bg-secondary/60 focus-visible:ring-1 focus-visible:ring-ring sm:grid-cols-[minmax(0,12rem)_minmax(0,1fr)_auto]"
    >
      <span className="col-start-1 row-start-1 flex min-w-0 items-center gap-1.5 text-[13px] text-foreground">
        <span className="truncate">{label}</span>
        {here > 0 && (
          <span className="flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
            <LiveDot />{fmtInt(here)} now
          </span>
        )}
      </span>
      <div className="col-span-2 row-start-2 flex h-3 min-w-0 items-center sm:col-span-1 sm:col-start-2 sm:row-start-1">
        {width > 0 && <div className="h-full rounded-r-[4px] bg-foreground/80" style={{ width: `${width}%` }} />}
        {lostWidth > 0 && (
          <div className="ml-[2px] h-full rounded-r-[4px] bg-foreground/15" style={{ width: `calc(${lostWidth}% - 2px)` }} />
        )}
      </div>
      <span className="col-start-2 row-start-1 flex items-center justify-end gap-2 text-xs tabular sm:col-start-3 sm:w-36">
        {lost > 0 && (
          <span className="flex items-center gap-0.5 text-muted-foreground">
            {severity === 'critical' ? <AlertTriangle className="h-3 w-3 text-red-600" aria-label="Big drop" />
              : severity === 'warning' ? <TrendingDown className="h-3 w-3 text-amber-600" aria-label="Drop" /> : null}
            −{fmtInt(lost)}
          </span>
        )}
        <span className="font-medium text-foreground">{fmtInt(count)}</span>
        <span className="w-9 text-right text-muted-foreground">{fmtPct(pctOf(count, base), 0)}</span>
      </span>

      <div className="pointer-events-none absolute right-2 top-full z-20 mt-1 hidden w-56 rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-lg group-hover:block group-focus-visible:block">
        <p className="font-semibold text-foreground">{fmtInt(count)} {count === 1 ? 'person' : 'people'}</p>
        <p className="text-muted-foreground">{label}</p>
        <p className="mt-1.5 text-muted-foreground">
          <span className="font-medium text-foreground">{fmtPct(pctOf(count, base))}</span> of everyone who started
        </p>
        <p className="text-muted-foreground">
          Lost here: <span className="font-medium text-foreground">{fmtInt(lost)}</span>{lost > 0 && ` (${fmtPct(drop)} of the step before)`}
        </p>
        {here > 0 && <p className="text-muted-foreground">On this step right now: <span className="font-medium text-foreground">{fmtInt(here)}</span></p>}
      </div>
    </li>
  );
}

function StageHeader({ label, from, to }) {
  return (
    <li className="flex items-baseline justify-between gap-2 px-2 pb-1 pt-4 first:pt-0">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-[11px] tabular text-muted-foreground">
        {fmtInt(from)} → {fmtInt(to)}{from > to && ` · lost ${fmtInt(from - to)}`}
      </span>
    </li>
  );
}

function Funnel({ data, hereByStep }) {
  const steps = data.steps || [];
  const base = data.total_users || 0;
  const out = data.outcomes || {};
  const paywall = out.paywall ?? 0;

  const rows = [];
  STAGES.forEach(([key, label]) => {
    const inStage = steps.filter((s) => s.stage === key);
    if (!inStage.length) return;
    const before = steps[inStage[0].step - 1];
    rows.push(<StageHeader key={`h-${key}`} label={label} from={before ? before.count : base} to={inStage[inStage.length - 1].count} />);
    inStage.forEach((s) => {
      const prev = steps[s.step - 1];
      rows.push(
        <FunnelRow
          key={s.step}
          label={humanize(s.name)}
          count={s.count}
          prev={prev ? prev.count : s.count}
          base={base}
          here={hereByStep[s.step] || 0}
        />,
      );
    });
  });
  rows.push(<StageHeader key="h-after" label="After the paywall" from={paywall} to={out.premium ?? 0} />);
  rows.push(<FunnelRow key="signed-in" label="Created an account" count={out.signed_in ?? 0} prev={paywall} base={base} />);
  rows.push(<FunnelRow key="premium" label="Bought premium" count={out.premium ?? 0} prev={out.signed_in ?? 0} base={base} />);

  return <ol className="-mx-2">{rows}</ol>;
}

/* ── Live ────────────────────────────────────────────────────────────────── */

function Person({ item, userById, onOpenCustomer }) {
  const u = item.user_id ? userById[item.user_id] : null;
  const name = u ? displayName(u) : `Visitor ${item.person}`;
  return u && onOpenCustomer ? (
    <button type="button" onClick={() => onOpenCustomer(item.user_id)} className="truncate font-medium text-foreground underline-offset-2 hover:underline">
      {name}
    </button>
  ) : (
    <span className="truncate font-medium text-foreground">{name}</span>
  );
}

const meta = (item) => [
  DEVICE[item.device] || (item.device && humanize(item.device)),
  item.language?.toUpperCase(),
].filter(Boolean).join(' · ');

function LivePanel({ live, userById, onOpenCustomer, lastStep }) {
  const d = live.data;
  return (
    <Panel
      flush
      title={<span className="flex items-center gap-2"><LiveDot on={!live.error} />Live</span>}
      description={`Updates every ${LIVE_EVERY_MS / 1000} seconds`}
      actions={<span className="text-[11px] text-muted-foreground">Updated <Since at={live.updatedAt} /></span>}
    >
      {live.error && !d ? (
        <div className="p-4"><ErrorState error={live.error} onRetry={live.refetch} /></div>
      ) : !d ? (
        <RowsSkeleton rows={5} />
      ) : (
        <div className="divide-y divide-border">
          <section className="px-4 py-3">
            <h3 className="text-xs font-semibold text-foreground">
              In onboarding now <span className="font-normal text-muted-foreground">· a step in the last {d.window_minutes} min</span>
            </h3>
            {d.active.length === 0 ? (
              <p className="mt-2 text-xs text-muted-foreground">Nobody right now.</p>
            ) : (
              <ul className="mt-2 space-y-2.5">
                {d.active.map((a) => (
                  <li key={a.person} className="text-xs">
                    <div className="flex items-baseline justify-between gap-2">
                      <Person item={a} userById={userById} onOpenCustomer={onOpenCustomer} />
                      <span className="shrink-0 text-muted-foreground">{timeAgo(a.last_at)}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-secondary">
                        <div className="h-full rounded-r-[4px] bg-emerald-500" style={{ width: `${((a.step + 1) / (lastStep + 1)) * 100}%` }} />
                      </div>
                      <span className="shrink-0 text-muted-foreground">{humanize(a.step_name)}</span>
                    </div>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">{meta(a)} · step {a.step + 1} of {lastStep + 1}</p>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="px-4 py-3">
            <h3 className="text-xs font-semibold text-foreground">
              Latest steps <span className="font-normal text-muted-foreground">· last 24 hours</span>
            </h3>
            {d.events.length === 0 ? (
              <p className="mt-2 text-xs text-muted-foreground">No onboarding steps in the last 24 hours.</p>
            ) : (
              <ul className="mt-1 max-h-[26rem] overflow-y-auto">
                {d.events.map((e) => (
                  // Keys are stable, so only steps that just arrived mount and slide in.
                  <li
                    key={`${e.person}:${e.step}:${e.at}`}
                    className="flex items-start justify-between gap-2 border-b border-border/60 py-2 text-xs duration-500 animate-in fade-in slide-in-from-top-1 last:border-0"
                  >
                    <div className="min-w-0">
                      <p className="flex min-w-0 gap-1">
                        <Person item={e} userById={userById} onOpenCustomer={onOpenCustomer} />
                        <span className="shrink-0 text-muted-foreground">reached</span>
                      </p>
                      <p className="truncate text-foreground">{humanize(e.step_name)}</p>
                      <p className="text-[11px] text-muted-foreground">{meta(e)}</p>
                    </div>
                    <span className="shrink-0 text-[11px] text-muted-foreground">{timeAgo(e.at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </Panel>
  );
}

/* ── Tab ─────────────────────────────────────────────────────────────────── */

export default function OnboardingFunnel({ range, onOpenCustomer }) {
  const funnel = useQuery(
    `kpi:onboarding:${range.start}:${range.end}`,
    () => api.get(`/admin/kpi/onboarding-funnel?start=${range.start}&end=${range.end}&days=${range.days}`),
    { maxAge: 60_000 },
  );
  const live = useQuery('kpi:onboarding-live', () => api.get('/admin/kpi/onboarding-live'), { maxAge: 5_000 });
  const { data: users } = useUsers();
  const userById = useMemo(() => Object.fromEntries((users || []).map((u) => [u.user_id, u])), [users]);

  usePolling(live.refetch, LIVE_EVERY_MS);
  usePolling(funnel.refetch, FUNNEL_EVERY_MS, range.end >= isoDay(new Date())); // only a range that includes today can change

  const hereByStep = useMemo(() => {
    const m = {};
    (live.data?.active || []).forEach((a) => { m[a.step] = (m[a.step] || 0) + 1; });
    return m;
  }, [live.data]);

  const f = funnel.data;
  const started = f?.total_users || 0;
  const out = f?.outcomes || {};
  const lastStep = Math.max((f?.steps?.length || 25) - 1, 1);

  if (funnel.error && !f) return <ErrorState error={funnel.error} onRetry={funnel.refetch} />;

  return (
    <div className="space-y-4">
      {!f ? <StatSkeletons count={4} /> : (
        <StatGrid>
          <StatCard
            label="Started onboarding"
            value={fmtInt(started)}
            hint={f.direct_to_paywall ? `+${fmtInt(f.direct_to_paywall)} straight to paywall` : 'In this period'}
          />
          <StatCard label="Reached the paywall" value={fmtInt(out.paywall)} hint={`${fmtPct(pctOf(out.paywall, started), 0)} of starters`} />
          <StatCard label="Created an account" value={fmtInt(out.signed_in)} hint={`${fmtPct(pctOf(out.signed_in, out.paywall), 0)} of paywall`} />
          <StatCard label="Bought premium" value={fmtInt(out.premium)} hint={`${fmtPct(pctOf(out.premium, out.signed_in), 0)} of accounts`} />
        </StatGrid>
      )}

      <div className="grid items-start gap-4 lg:grid-cols-3">
        <Panel
          className="lg:col-span-2"
          title="Funnel"
          description="Out of everyone who started, how many got at least this far. The faint piece after a bar is who that step lost. Test runs and staff accounts are left out."
          actions={(
            <span className="flex items-center gap-3 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1"><TrendingDown className="h-3 w-3 text-amber-600" />loses over 10%</span>
              <span className="flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-red-600" />over 25%</span>
            </span>
          )}
        >
          {!f ? <RowsSkeleton rows={8} /> : started === 0 ? (
            <EmptyState icon={Radio} title="Nobody started onboarding in this period" />
          ) : (
            <Funnel data={f} hereByStep={hereByStep} />
          )}
        </Panel>
        <LivePanel live={live} userById={userById} onOpenCustomer={onOpenCustomer} lastStep={lastStep} />
      </div>
    </div>
  );
}
